import React, { useState } from "react";
import { apiClient } from "../App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * SettleBalanceDialog
 *
 * Opens a confirmation modal allowing the admin to clear a user's balance to 0
 * by recording a "settlement" payment for the exact owed/credited amount.
 *
 * Props:
 *   open, onOpenChange
 *   user      : { id, name, balance, email }
 *   onSettled : optional callback after successful settlement
 */
export const SettleBalanceDialog = ({ open, onOpenChange, user, onSettled }) => {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const balance = Number(user.balance || 0);
  const absBalance = Math.abs(balance);
  const isCredit = balance > 0; // System owes the user
  const isAlreadySettled = absBalance < 0.005;

  const handleSettle = async () => {
    if (isAlreadySettled || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiClient.post(`/admin/users/${user.id}/settle`, {
        note: note.trim() || null,
      });
      toast.success(
        `${user.name}'s balance settled (₹${Math.abs(res.data.settlement_amount).toFixed(2)})`
      );
      onOpenChange(false);
      setNote("");
      if (onSettled) onSettled(res.data);
    } catch (err) {
      console.error("Settle failed:", err);
      const msg = err?.response?.data?.detail || "Failed to settle balance";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="settle-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#059669]" />
            Settle Balance
          </DialogTitle>
          <DialogDescription>
            This clears <strong>{user.name}</strong>&apos;s outstanding balance to ₹0.00.
            A settlement entry will be recorded in their ledger and the activity log.
          </DialogDescription>
        </DialogHeader>

        {isAlreadySettled ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
            This account is already at ₹0.00 — nothing to settle.
          </div>
        ) : (
          <>
            <div
              className={`rounded-xl border p-4 ${
                isCredit
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}
              data-testid="settle-balance-preview"
            >
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                Current balance
              </p>
              <p
                className={`text-2xl font-bold ${
                  isCredit ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {isCredit ? "+" : "−"}₹{absBalance.toFixed(2)}
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {isCredit
                  ? `You owe ${user.name} ₹${absBalance.toFixed(2)}. Settling will record a ₹${absBalance.toFixed(2)} payout.`
                  : `${user.name} owes ₹${absBalance.toFixed(2)}. Settling will record their payment of ₹${absBalance.toFixed(2)}.`}
              </p>
            </div>

            <div className="space-y-2 mt-3">
              <Label htmlFor="settle-note">Note (optional)</Label>
              <Textarea
                id="settle-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Paid in cash on 1 Jan"
                rows={2}
                className="rounded-xl"
                data-testid="settle-note-input"
              />
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="settle-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSettle}
            disabled={isAlreadySettled || submitting}
            className="bg-[#059669] hover:bg-[#047857] text-white"
            data-testid="settle-confirm-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Settling…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm Settlement
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettleBalanceDialog;
